import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";

import DownloadEntitiesButton from "@app/component/DownloadEntitiesModal/DownloadEntitiesButton";
import SelectColumnsButton from "@app/component/SelectColumnsPane/SelectColumnsButton";
import { downloadTripList } from "@app/scene/reports/store/mileageTracking/actions";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import tripFieldModel, { TripFieldName } from "@app/util/fieldModel/tripFieldModel";

import MonthFilter from "./filters/MonthFilter";
import RecordFilter from "./filters/RecordFilter";
import TypeFilter from "./filters/TypeFilter";
import UserFilter from "./filters/UserFilter";
import styles from "./Toolbar.module.scss";

const isListField = (field: IField) => !field.hasFeature(FieldFeature.NON_LIST_VIEW);

export interface ToolbarProps {
  canDownload: boolean;
  columns: ColumnModel;
  totalFilteredRecords: number;
}

interface Props extends ToolbarProps {
  onDownload: typeof downloadTripList;
}

const Toolbar: FC<Props> = ({ canDownload, columns, onDownload, totalFilteredRecords }) => {
  const handledDownload = useCallback(
    (columns: ColumnModel) => {
      onDownload({ columns });
    },
    [onDownload]
  );

  const defaultColumnList = useMemo(() => {
    const finishTimeField = tripFieldModel.getByName(TripFieldName.FINISH_TIME);
    const startColumnIndex = columns.findIndex(
      ({ field }) => field.name === TripFieldName.START_TIME
    );
    const endColumn = columns.find(({ field }) => field.name === TripFieldName.FINISH_TIME);
    if (finishTimeField && startColumnIndex >= 0 && !endColumn) {
      // insert finish time column after start column
      return [
        ...columns.slice(0, startColumnIndex + 1),
        { ...columns[startColumnIndex], field: finishTimeField },
        ...columns.slice(startColumnIndex + 1),
      ];
    } else {
      return columns;
    }
  }, [columns]);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <MonthFilter />
        <UserFilter />
        <TypeFilter />
        <RecordFilter />
      </div>
      <div className={styles.actions}>
        {canDownload && (
          <DownloadEntitiesButton
            defaultColumnList={defaultColumnList}
            entityType={EntityType.TRIP}
            onDownload={handledDownload}
            total={totalFilteredRecords}
          />
        )}
        <SelectColumnsButton
          columnPaneProps={{ showCategories: false }}
          entityType={EntityType.TRIP}
          fieldFilter={isListField}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
});

const mapDispatchToProps = {
  onDownload: downloadTripList,
};

export default connect(mapStateToProps, mapDispatchToProps)(Toolbar);
